import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeSlash } from "@phosphor-icons/react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const routeFor = (role) => (role === "admin" ? "/admin" : role === "owner" ? "/owner" : "/");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      toast.success("Welcome back!");
      navigate(routeFor(res.user.role));
    } else {
      setError(res.error);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-md animate-fade-up rounded-3xl border border-stone-200 bg-white p-8 shadow-sm">
        <Link to="/" className="inline-block" data-testid="app-logo">
          <p className="font-heading text-3xl font-extrabold tracking-tight text-primary">Avinya <span className="font-semibold text-stone-800">Bookings</span></p>
          <p className="mt-1.5 text-[11px] font-semibold uppercase tracking-[0.32em] text-amber-600">Book · Relax · Celebrate</p>
        </Link>
        <h1 className="mt-6 font-heading text-2xl font-semibold text-stone-900">Welcome back</h1>
        <p className="text-sm text-stone-500">Login to continue your journey.</p>

        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <Label htmlFor="email">Email</Label>
            <Input id="email" data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 rounded-xl" placeholder="you@example.com" />
          </div>
          <div>
            <Label htmlFor="password">Password</Label>
            <div className="relative mt-1">
              <Input id="password" data-testid="login-password" type={show ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)} className="rounded-xl pr-10" placeholder="••••••••" />
              <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400">
                {show ? <EyeSlash size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>
          {error && <p data-testid="login-error" className="text-sm text-destructive">{error}</p>}
          <div className="text-right">
            <Link to="/forgot-password" data-testid="forgot-link" className="text-sm font-medium text-primary hover:underline">Forgot password?</Link>
          </div>
          <Button data-testid="login-submit" type="submit" disabled={loading} className="w-full rounded-xl bg-primary py-6 text-base hover:bg-green-900">
            {loading ? "Logging in..." : "Login"}
          </Button>
        </form>

        <p className="mt-6 text-center text-sm text-stone-500">
          New to Avinya? <Link to="/register" data-testid="go-register" className="font-semibold text-primary hover:underline">Create an account</Link>
        </p>
      </div>
    </div>
  );
}
